import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { socket, connectSocket } from '../../socket';
import { getPlayerId, getSavedName, saveName } from '../../utils/playerIdentity';
// import { fetchHealth } from '../../utils/api';
import RulesModal from './RulesModal';
import Tutorial from './Tutorial';
import Profile from './Profile';
import Leaderboard from './Leaderboard';
import aceLogo from '../../assets/ace-logo.png';
import './Home.css';

interface HomeProps {
  onRoomCreated: (code: string, name: string) => void;
  onRoomJoined: (code: string, name: string) => void;
  onRejoinGame: (code: string, name: string) => void;
  initialRoomCode?: string;
}

const Home: React.FC<HomeProps> = ({ onRoomCreated, onRoomJoined, onRejoinGame, initialRoomCode }) => {
  const [tab, setTab] = useState<'create' | 'join'>(initialRoomCode ? 'join' : 'create');
  const [playerName, setPlayerName] = useState(getSavedName());
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [numComputers, setNumComputers] = useState(0);
  const [roomCode, setRoomCode] = useState(initialRoomCode || '');
  const [turnDuration, setTurnDuration] = useState(30);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(socket.connected);
  // const [statsStatus, setStatsStatus] = useState<'checking' | 'online' | 'offline'>('checking');

  const actionRef = useRef<'create' | 'join' | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const roomCodeInputRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef('');
  nameRef.current = playerName;
  const onCreatedRef = useRef(onRoomCreated);
  onCreatedRef.current = onRoomCreated;
  const onJoinedRef = useRef(onRoomJoined);
  onJoinedRef.current = onRoomJoined;
  const onRejoinRef = useRef(onRejoinGame);
  onRejoinRef.current = onRejoinGame;

  const clearPendingAction = () => {
    actionRef.current = null;
    setLoading(false);
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
  };

  useEffect(() => {
    connectSocket();

    const onCreated = ({ roomCode }: { roomCode: string }) => {
      if (actionRef.current !== 'create') return;
      clearPendingAction();
      onCreatedRef.current(roomCode, nameRef.current);
    };

    const onUpdate = (data: any) => {
      if (actionRef.current !== 'join') return;
      clearPendingAction();
      onJoinedRef.current(data.code, nameRef.current);
    };

    const onRejoin = ({ roomCode }: { roomCode: string }) => {
      if (actionRef.current !== 'join') return;
      clearPendingAction();
      onRejoinRef.current(roomCode, nameRef.current);
    };

    const onError = ({ message }: { message: string }) => {
      clearPendingAction();
      setError(message);
    };

    const onConnect = () => setConnected(true);
    const onDisconnect = () => {
      setConnected(false);
      if (actionRef.current) {
        clearPendingAction();
        setError('Connection lost — please try again');
      }
    };

    socket.on('room_created', onCreated);
    socket.on('room_update', onUpdate);
    socket.on('rejoin_game', onRejoin);
    socket.on('error_msg', onError);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    return () => {
      socket.off('room_created', onCreated);
      socket.off('room_update', onUpdate);
      socket.off('rejoin_game', onRejoin);
      socket.off('error_msg', onError);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   fetchHealth().then((h) => {
  //     setStatsStatus(h?.redis ? 'online' : 'offline');
  //   });
  // }, []);

  const startTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (actionRef.current) {
        clearPendingAction();
        setError('Server took too long to respond — please try again');
      }
    }, 8000);
  };

  const handleCreate = () => {
    if (!playerName.trim()) { setError('Enter your name'); return; }
    if (!connected) { setError('Not connected to server — waiting for reconnection'); return; }
    setError('');
    setLoading(true);
    actionRef.current = 'create';
    startTimeout();
    const name = playerName.trim();
    saveName(name);
    socket.emit('create_room', {
      playerName: name,
      playerId: getPlayerId(),
      maxPlayers,
      numComputers,
      settings: { turnDuration },
    });
  };

  const handleJoin = () => {
    if (!playerName.trim()) { setError('Enter your name'); return; }
    if (!roomCode.trim()) { setError('Enter the room code'); return; }
    if (!connected) { setError('Not connected to server — waiting for reconnection'); return; }
    setError('');
    setLoading(true);
    actionRef.current = 'join';
    startTimeout();
    const name = playerName.trim();
    saveName(name);
    socket.emit('join_room', {
      roomCode: roomCode.trim().toUpperCase(),
      playerName: name,
      playerId: getPlayerId(),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      tab === 'create' ? handleCreate() : handleJoin();
    }
  };

  const humanSlots = maxPlayers - numComputers;
  const normalizedRoomCode = roomCode.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 4);
  const [showRules, setShowRules] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  return (
    <div className="home-screen">
      <motion.div
        className="home-logo-wrapper"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <img src={aceLogo} alt="ACE" className="home-logo" />
        <p className="home-subtitle">Race to Empty</p>
        <div className={`home-connection-pill ${connected ? 'online' : 'offline'}`}>
          <span className="home-connection-dot" />
          {connected ? 'Server Connected' : 'Reconnecting...'}
        </div>
      </motion.div>

      <motion.div
        className="home-card"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="home-card-body">
        {/* Name input */}
        <div className="home-field">
          <label htmlFor="player-name-input">Your Name</label>
          <input
            id="player-name-input"
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter your name"
            maxLength={16}
          />
        </div>

        {/* Tabs */}
        <div className="home-tabs">
          <button
            className={`tab ${tab === 'create' ? 'active' : ''}`}
            onClick={() => { setTab('create'); setError(''); }}
          >
            Create Game
          </button>
          <button
            className={`tab ${tab === 'join' ? 'active' : ''}`}
            onClick={() => { setTab('join'); setError(''); setTimeout(() => roomCodeInputRef.current?.focus(), 100); }}
          >
            Join Game
          </button>
        </div>

        {/* Create tab */}
        {tab === 'create' && (
          <motion.div
            className="tab-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="home-field">
              <label id="total-players-label">Total Players</label>
              <div className="stepper" role="group" aria-labelledby="total-players-label">
                <button aria-label="Decrease total players" onClick={() => setMaxPlayers((v) => Math.max(3, v - 1))}>−</button>
                <span aria-live="polite">{maxPlayers}</span>
                <button aria-label="Increase total players" onClick={() => setMaxPlayers((v) => Math.min(8, v + 1))}>+</button>
              </div>
            </div>
            <div className="home-field">
              <label id="computer-players-label">Computer Players</label>
              <div className="stepper" role="group" aria-labelledby="computer-players-label">
                <button aria-label="Decrease computer players" onClick={() => setNumComputers((v) => Math.max(0, v - 1))}>−</button>
                <span aria-live="polite">{numComputers}</span>
                <button aria-label="Increase computer players" onClick={() => setNumComputers((v) => Math.min(maxPlayers - 1, v + 1))}>+</button>
              </div>
            </div>
            <div className="home-field">
              <label>Turn Timer</label>
              <div className="timer-options">
                {[
                  { value: 15, label: '15s' },
                  { value: 30, label: '30s' },
                  { value: 45, label: '45s' },
                  { value: 60, label: '60s' },
                  { value: 0, label: 'Off' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    className={`timer-option ${turnDuration === opt.value ? 'active' : ''}`}
                    onClick={() => setTurnDuration(opt.value)}
                    type="button"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="home-hint">
              {humanSlots} human {humanSlots === 1 ? 'slot' : 'slots'} &middot; {numComputers} computer{numComputers !== 1 ? 's' : ''}
              {turnDuration > 0 ? ` · ${turnDuration}s per turn` : ' · No time limit'}
            </p>
          </motion.div>
        )}

        {/* Join tab */}
        {tab === 'join' && (
          <motion.div
            className="tab-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="home-field">
              <label htmlFor="room-code-input">Room Code</label>
              <input
                id="room-code-input"
                ref={roomCodeInputRef}
                type="text"
                value={normalizedRoomCode}
                onChange={(e) => setRoomCode(e.target.value.replace(/[^A-Z0-9]/gi, '').toUpperCase())}
                onKeyDown={handleKeyDown}
                placeholder="XXXX"
                maxLength={4}
                className="room-code-input"
                autoCapitalize="characters"
                autoCorrect="off"
              />
            </div>
          </motion.div>
        )}

        <div className="home-summary-row">
          <div className="home-summary-chip">
            <strong>{tab === 'create' ? maxPlayers : normalizedRoomCode || '----'}</strong>
            <span>{tab === 'create' ? 'Total seats' : 'Room code'}</span>
          </div>
          <div className="home-summary-chip">
            <strong>{tab === 'create' ? humanSlots : 'Mobile'}</strong>
            <span>{tab === 'create' ? 'Human slots' : 'Optimized UI'}</span>
          </div>
          <div className="home-summary-chip">
            <strong>{tab === 'create' ? (turnDuration > 0 ? `${turnDuration}s` : 'Off') : 'Live'}</strong>
            <span>{tab === 'create' ? 'Turn timer' : 'Realtime rooms'}</span>
          </div>
        </div>

        <div className="home-action-area">
          <motion.button
            className="home-action-btn"
            onClick={tab === 'create' ? handleCreate : handleJoin}
            disabled={loading}
            whileHover={!loading ? { scale: 1.03 } : {}}
            whileTap={!loading ? { scale: 0.97 } : {}}
          >
            {loading ? (tab === 'create' ? 'Creating...' : 'Joining...') : (tab === 'create' ? 'Create Room' : 'Join Room')}
          </motion.button>
          <p className="home-action-note">
            {tab === 'create'
              ? 'Share the room link and start whenever everyone is ready.'
              : 'Use the 4-character room code from the host.'}
          </p>
          {error && <p className="home-error">{error}</p>}
        </div>

        <div className="home-bottom-btns">
          <button className="home-rules-btn" onClick={() => setShowRules(true)}>
            Rules
          </button>
          <button className="home-rules-btn tutorial-btn" onClick={() => setShowTutorial(true)}>
            Tutorial
          </button>
        </div>

        <div className="home-bottom-btns">
          <button className="home-rules-btn profile-btn" onClick={() => setShowProfile(true)}>
            Profile
          </button>
          <button className="home-rules-btn leaderboard-btn" onClick={() => setShowLeaderboard(true)}>
            Leaderboard
          </button>
        </div>

        <div className="home-info">
          <p>🃏 Last player with cards loses</p>
          <p>⚡ 3–8 players supported</p>
        </div>
        </div>
      </motion.div>

      <RulesModal open={showRules} onClose={() => setShowRules(false)} />
      <Tutorial open={showTutorial} onClose={() => setShowTutorial(false)} />
      <Profile open={showProfile} onClose={() => setShowProfile(false)} />
      <Leaderboard open={showLeaderboard} onClose={() => setShowLeaderboard(false)} />
    </div>
  );
};

export default Home;
